import { useMemo } from "react";
import { HasAccess } from "@/components/common/has-access";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pill, RefreshCw, XIcon } from "lucide-react";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import usePrescriptions from "@/features/prescriptions/hooks/use-prescriptions";
import { PrescriptionCard } from "@/features/prescriptions/ui/prescription-card";
import { ContentLayout } from "@/components/common/content-layout";
import { Button } from "@/components/ui/button";
import { useRefreshDrugCache } from "@/features/prescriptions/hooks/use-refresh-drug-cache";
import PrescriptionFilter from "@/features/prescriptions/ui/prescription-filter";
import { SearchBar } from "@/components/common/search-bar";

const PAGE_SIZE = 20; // Must match backend pageSize

interface PrescriptionFilters {
  status: string;
  document_type: string;
  professional_id: string;
  start_date: string;
  end_date: string;
}

export default function Prescriptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read filter values from URL params
  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || "ALL";
  const documentType = searchParams.get("document_type") || "ALL";
  const professionalId = searchParams.get("professional_id") || "ALL";
  const startDate = searchParams.get("start_date") || "";
  const endDate = searchParams.get("end_date") || "";

  const currentFilters = useMemo(
    () => ({
      status,
      document_type: documentType,
      professional_id: professionalId,
      start_date: startDate,
      end_date: endDate,
    }),
    [status, documentType, professionalId, startDate, endDate]
  );

  // Convert "ALL" to undefined for API calls
  const statusForApi = status && status !== "ALL" ? status : undefined;
  const documentTypeForApi = documentType && documentType !== "ALL" ? documentType : undefined;
  const professionalIdForApi =
    professionalId && professionalId !== "ALL" ? professionalId : undefined;

  const { data, error, isFetching, isLoading } = usePrescriptions(
    page,
    search || undefined,
    statusForApi,
    documentTypeForApi,
    undefined,
    startDate || undefined,
    endDate || undefined,
    professionalIdForApi
  );

  const refreshDrugCacheMutation = useRefreshDrugCache();

  const prescriptions = data?.prescriptions || [];
  const currentPage = data?.currentPage ?? 0;
  const backendLastPage = data?.lastPage ?? 0; // 0-based index from backend
  const totalPages = backendLastPage + 1; // Total number of pages (1-based)
  const total = data?.total ?? 0;

  const handlePageChange = (newPage: number) => {
    // newPage is 0-based from the component
    if (newPage >= 0 && newPage <= backendLastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  };

  const handleApplyFilters = (filters: PrescriptionFilters) => {
    const newParams = new URLSearchParams();
    newParams.set("page", "0");

    if (filters.status && filters.status !== "ALL") {
      newParams.set("status", filters.status);
    }

    if (filters.document_type && filters.document_type !== "ALL") {
      newParams.set("document_type", filters.document_type);
    }

    if (filters.professional_id && filters.professional_id !== "ALL") {
      newParams.set("professional_id", filters.professional_id);
    }

    if (filters.start_date) {
      newParams.set("start_date", filters.start_date);
    }

    if (filters.end_date) {
      newParams.set("end_date", filters.end_date);
    }

    setSearchParams(newParams);
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-3">
          <PrescriptionFilter
            onApplyFilters={handleApplyFilters}
            currentFilters={currentFilters}
          />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Prescriptions..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
            />
          </div>
          <HasAccess permission="write">
          <Button
            onClick={() => refreshDrugCacheMutation.mutate(undefined)}
            disabled={refreshDrugCacheMutation.isPending}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw 
              className={`h-4 w-4 ${refreshDrugCacheMutation.isPending ? 'animate-spin' : ''}`} 
            />
            Refresh Drug Cache
          </Button>
          </HasAccess>
        </div>
      }
      data={prescriptions}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load prescriptions"
      emptyState={{
        message: "No prescriptions found",
        icon: <Pill className="h-8 w-8 text-muted-foreground" />,
      }}
    >
      <PrescriptionCard prescriptions={prescriptions} />
      {total > 0 && (
        <div className="flex flex-col items-center gap-4 mt-6">
          {totalPages > 1 && (
            <CustomPaginationComponent
              currentPage={currentPage}
              lastPage={totalPages}
              onPageChange={handlePageChange}
            />
          )}
          <div className="text-sm text-muted-foreground">
            Showing {prescriptions.length > 0 ? currentPage * PAGE_SIZE + 1 : 0} -{" "}
            {Math.min((currentPage + 1) * PAGE_SIZE, total)} of {total} prescriptions
          </div>
        </div>
      )}
    </ContentLayout>
  );
}
